import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import dayjs from "dayjs"
import {AppThunk} from "../../rootReducer"
import {getAccessToken, getSelectedRegionId} from "../../utils/common"
import httpService from "../../utils/httpService"
import {setToastr} from "../App/appSlice"

export interface TestPackage {
    testPackageID: number
    testPackageName: string
    laneFee?: number
    active: boolean
    notes: string
    testFee: number
    duration: number
    tests: {item1: number; item2: string}[]
    testPackageDescription: string
    effectiveDate: Date
}
export interface TestPackageGrid {
    testPackageID: number
    testPackageName: string
    laneFee?: string
    active: boolean
    notes: string
    testFee: string
    duration: string
    tests: {item1: number; item2: string}[]
    testPackageDescription: string
    effectiveDate: Date
    hasBookings: boolean
    dvsaTestCosts: number[]
}
export interface DvsaTestCost {
    dvsatestCostId: number
}

export interface TestPackageDetails extends TestPackage {
    hasBookings: boolean
    dvsaTestCosts: number[]
}

type FeesState = {
    isAddEditModalToggled: boolean
    testPackages: TestPackageGrid[]
    testPackageDetails: TestPackageDetails
    testPackageTests: number[]
    // errorToastr: {isActive: boolean; error: string}
    testPackagesLoading: boolean
    testPackageDetailsLoading: boolean
    impactModal: boolean
    testPackageBookingImpactGrid: []
    testPackageBookingLoader: boolean
    initialEffectiveDate: Date
}
export interface TestPackageBookingImpactGrid {
    bookingID: number
    bookingRef: string
    bookingStatusName: string
    laneFee: string
    testFee: string
    duration: string
    startDate: Date | null
}

const initialState: FeesState = {
    isAddEditModalToggled: false,
    testPackages: [],
    testPackageDetails: {} as TestPackageDetails,
    testPackageTests: [],
    // errorToastr: {isActive: false, error: ""},
    testPackagesLoading: false,
    testPackageDetailsLoading: false,
    impactModal: false,
    testPackageBookingImpactGrid: [],
    testPackageBookingLoader: false,
    initialEffectiveDate: new Date(),
}

const feesSlice = createSlice({
    name: "fees",
    initialState,
    reducers: {
        setTestPackageDetails(state, action: PayloadAction<TestPackageDetails>) {
            state.testPackageDetails = action.payload
            if (action.payload.dvsaTestCosts) state.testPackageTests = action.payload.dvsaTestCosts.map(t => t)
            else state.testPackageTests = []
        },
        setTestPackageTests(state, action: PayloadAction<number[]>) {
            state.testPackageTests = action.payload
        },
        setTestPackages(state, action: PayloadAction<TestPackageGrid[]>) {
            state.testPackages = action.payload
        },
        showAddEditTestPackageModal(state, action: PayloadAction<boolean>) {
            state.isAddEditModalToggled = action.payload
        },

        // setErrorToastr(state, action: PayloadAction<{isActive: boolean; error: string}>) {
        //     state.errorToastr = action.payload
        // },
        setTestPackageDetailsLoading(state, action: PayloadAction<boolean>) {
            state.testPackageDetailsLoading = action.payload
        },
        setTestPackagesLoading(state, action: PayloadAction<boolean>) {
            state.testPackagesLoading = action.payload
        },
        toggleImpactModal(state, action: PayloadAction<boolean>) {
            state.impactModal = action.payload
        },
        setTestPackageBookingImpactGrid(state, action: PayloadAction<any>) {
            state.testPackageBookingImpactGrid = action.payload
        },
        setTestPackageBookingLoader(state, action: PayloadAction<boolean>) {
            state.testPackageBookingLoader = action.payload
        },
        setInitialEffectiveDate(state, action: PayloadAction<Date>) {
            state.initialEffectiveDate = action.payload
        },
    },
})

export const {
    setTestPackages,
    showAddEditTestPackageModal,
    setTestPackageDetails,
    setTestPackageTests,
    // setErrorToastr,
    setTestPackageDetailsLoading,
    setTestPackagesLoading,
    toggleImpactModal,
    setTestPackageBookingImpactGrid,
    setTestPackageBookingLoader,
    setInitialEffectiveDate,
} = feesSlice.actions

export default feesSlice.reducer

// #region Thunks
export const fetchTestPackages =
    (selectedDealerId: number): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setTestPackagesLoading(true))
        httpService(getAccessToken(getState()))
            .get(`regions/${getSelectedRegionId(getState())}/dealers/${selectedDealerId}/testpackages`)
            .then(res => {
                dispatch(setTestPackagesLoading(false))
                const updatedtestpackage = res.data.map((x: TestPackageGrid) => ({
                    ...x,
                    effectiveDate:
                        x.effectiveDate !== null ? new Date(dayjs(x.effectiveDate).startOf("day").toDate()) : null,
                }))
                dispatch(setTestPackages(updatedtestpackage))
            })
            .catch(error => {
                dispatch(setTestPackagesLoading(false))
                dispatch(setTestPackages([]))
            })
    }

export const getTestPackageDetails =
    (selectedDealerId: number, testPackageId: number): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setTestPackageDetailsLoading(true))
        httpService(getAccessToken(getState()))
            .get(`regions/${getSelectedRegionId(getState())}/dealers/${selectedDealerId}/testpackages/${testPackageId}`)
            .then(res => {
                const updatedTestPackage = {
                    ...res.data,
                    effectiveDate:
                        res.data.effectiveDate !== null && dayjs(res.data.effectiveDate).startOf("day").toDate(),
                }
                dispatch(setTestPackageDetails(updatedTestPackage))
                dispatch(setTestPackageDetailsLoading(false))
            })
            .catch(error => {
                dispatch(setTestPackageDetailsLoading(false))
            })
    }

export const postTestPackageDetails =
    (selectedDealerId: number, testPackageDetails: any, isAccept: boolean): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setTestPackageDetailsLoading(true))

        isAccept && dispatch(setTestPackageBookingLoader(true))

        if (!testPackageDetails.testPackageID) testPackageDetails.testPackageID = 0
        testPackageDetails.dvsaTestCosts = getState().containers.fees.testPackageTests
        if (testPackageDetails.testPackageID === undefined || testPackageDetails.testPackageID === 0) {
            // add
            httpService(getAccessToken(getState()))
                .put(
                    `regions/${getSelectedRegionId(getState())}/dealers/${selectedDealerId}/testpackages`,
                    testPackageDetails,
                )
                .then(res => {
                    dispatch(showAddEditTestPackageModal(false))
                    dispatch(fetchTestPackages(selectedDealerId))
                    dispatch(setTestPackageDetailsLoading(false))
                })
                .catch(error => {
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: error.response.data.Value.Error,
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    )
                    dispatch(setTestPackageDetailsLoading(false))
                })
                .finally(() => {})
        } else {
            // edit
            httpService(getAccessToken(getState()))
                .post(
                    `regions/${getSelectedRegionId(getState())}/dealers/${selectedDealerId}/testpackages/${
                        testPackageDetails.testPackageID
                    }`,
                    testPackageDetails,
                )
                .then(res => {
                    if (isAccept) {
                        let upcomingBookingIDs = getState()
                            .containers.fees.testPackageBookingImpactGrid.map((x: any) => x.bookingID)
                            .map(Number)
                        dispatch(
                            setTestPackageFutureBooking(
                                selectedDealerId,
                                res.data,
                                upcomingBookingIDs,
                                testPackageDetails.laneFee,
                                testPackageDetails.testFee,
                            ),
                        )
                    } else {
                        dispatch(showAddEditTestPackageModal(false))
                        dispatch(fetchTestPackages(selectedDealerId))
                        dispatch(setTestPackageDetailsLoading(false))
                    }
                })
                .catch(error => {
                    dispatch(setTestPackageDetailsLoading(false))
                    dispatch(setTestPackageBookingLoader(false))
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: error.response.data.Value.Error,
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    )
                })
                .finally(() => {})
        }
    }

export const deleteTestPackageDetails =
    (selectedDealerId: number, testPackageId: number): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setTestPackageDetailsLoading(true))

        httpService(getAccessToken(getState()))
            .delete(
                `regions/${getSelectedRegionId(getState())}/dealers/${selectedDealerId}/testpackages/${testPackageId}`,
            )
            .then(() => {
                dispatch(setTestPackageDetailsLoading(false))
                dispatch(showAddEditTestPackageModal(false))
                dispatch(fetchTestPackages(selectedDealerId))
            })
            .catch(error => {
                dispatch(setTestPackageDetailsLoading(false))
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            })
            .finally(() => {})
    }

export const disableTestPackage =
    (selectedDealer: number, testPackageId: number, active: boolean): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setTestPackageDetailsLoading(true))
        const userId = getState().application.userInfo.idTokenClaims.oid
        httpService(getAccessToken(getState()))
            .put(
                `regions/${getSelectedRegionId(
                    getState(),
                )}/user/${userId}/testpackages/${testPackageId}?active=${active}`,
            )
            .then(() => dispatch(fetchTestPackages(selectedDealer)))
            .finally(() => {
                dispatch(showAddEditTestPackageModal(false))
                dispatch(setTestPackageDetailsLoading(false))
            })
    }

export const getTestPackageBookingImpactGrid =
    (selectedDealerId: number, testPackageDetails: any): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setTestPackageDetailsLoading(true))

        httpService(getAccessToken(getState()))
            .get(
                `regions/${getSelectedRegionId(getState())}/dealers/${selectedDealerId}/testpackages/${
                    testPackageDetails.testPackageID
                }/booking-impact?effectiveDate=${testPackageDetails.effectiveDate}`,
            )
            .then((res: any) => {
                if (res.data.length === 0) {
                    dispatch(postTestPackageDetails(selectedDealerId, testPackageDetails, false))
                } else {
                    let testPackageBookingImpactGrid: TestPackageBookingImpactGrid = res.data.map((tp: any) => ({
                        ...tp,
                        startDate: tp.startDate !== null ? new Date(dayjs(tp.startDate).toDate()) : null,
                        laneFee:
                            testPackageDetails.laneFee !== tp.laneFee
                                ? `£${tp.laneFee} -> £${testPackageDetails.laneFee}`
                                : `£${tp.laneFee}`,
                        testFee:
                            testPackageDetails.testFee !== tp.testFee
                                ? `£${tp.testFee} -> £${testPackageDetails.testFee}`
                                : `£${tp.testFee}`,
                        duration:
                            testPackageDetails.duration !== tp.duration
                                ? `${tp.duration} -> ${testPackageDetails.duration}`
                                : `${tp.duration}`,
                    }))
                    dispatch(setTestPackageBookingImpactGrid(testPackageBookingImpactGrid))
                }
            })
            .catch(() => dispatch(setTestPackageDetailsLoading(false)))
            .finally(() => {})
    }

export const setTestPackageFutureBooking =
    (
        selectedDealerId: number,
        futureTestPackageID: number,
        upcomingBookingIDs: number[],
        laneFee: number,
        testFee: number,
    ): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setTestPackageBookingLoader(true))

        httpService(getAccessToken(getState()))
            .put(
                `regions/${getSelectedRegionId(
                    getState(),
                )}/dealers/${selectedDealerId}/testpackages/${futureTestPackageID}/booking-impact`,
                {UpcomingBookingIDs: upcomingBookingIDs, Lanefee: laneFee, TestFee: testFee},
            )
            .then(res => {})
            .catch(() => {})
            .finally(() => {
                dispatch(toggleImpactModal(false))
                dispatch(setTestPackageBookingLoader(false))
                dispatch(showAddEditTestPackageModal(false))
                dispatch(fetchTestPackages(selectedDealerId))
                dispatch(setTestPackageDetailsLoading(false))
                dispatch(setTestPackageBookingImpactGrid([]))
            })
    }
